// ══════════════ CONSENTEMENT / ACCUSÉ RGPD SALARIÉS ══════════════
// SPEC_rgpd_consentement_v1.md (17/08/2026). Modale plein écran affichée côté salarié
// tant qu'aucun accusé n'existe pour la version courante de `rgpdDoc7`. Montée au niveau
// StaffUI (pas dépendante d'un onglet) : le cycle de vie React (montage à l'ouverture de
// l'appli) suffit à garantir la réapparition à chaque ouverture — même principe que
// AlerteTacheModal (v145), pas de code de persistance de "vu" supplémentaire.
//
// Décisions non négociables (voir SPEC §2, à ne pas rouvrir sans validation de Claire) :
//  - Pas de bouton "Je n'accepte pas" — accusé de réception uniquement, jamais un choix
//    binaire (base légale = exécution du contrat, pas le consentement RGPD).
//  - Après RGPD_REPORT_WINDOW_MS (24h) depuis la première apparition, la croix disparaît
//    — mais rien d'autre dans l'appli n'est jamais bloqué par ce composant.
//  - Anti-esquive = défilement jusqu'en bas + délai minimum (RGPD_SCROLL_DELAY_MS).
//    Pas de recopie de phrase (retenu explicitement contre, cf SPEC §2.3).
//  - AUCUNE fonction d'édition/suppression d'un accusé n'existe nulle part dans ce
//    fichier ni ailleurs — c'est ça qui garantit l'irréversibilité (SPEC §2.5), pas un
//    verrou technique. Ne jamais ajouter une telle fonction sans revalidation explicite.
import React, { useState, useEffect, useRef } from 'react';
import { supaGet, supaSet } from '../supabase.js';
import { now } from '../utils.js';
import { RGPD_SCROLL_DELAY_MS, RGPD_REPORT_WINDOW_MS } from '../constants.js';
import { tr } from './EncadrementRenforce.jsx';

// Accusé existant pour ce salarié sur cette version, ou null.
export const rgpdAckFor = (rgpdAck, sid, version) =>
  (rgpdAck || []).find(a => a.sid === sid && a.version === version) || null;

function RgpdInfoModal({ sid, me, rgpdDoc, rgpdAck, setRgpdAck, rgpdPending, setRgpdPending }) {
  const langue = me?.langue === 'es' ? 'es' : 'fr';
  const version = rgpdDoc?.version;
  const already = rgpdAckFor(rgpdAck, sid, version);

  // ── Premier vu : écriture read-before-write sur rgpdPending7, même patron que
  // onAssist (StaffUI.jsx) — nécessaire pour calculer les 24h côté serveur, donc
  // valable même si le salarié change d'appareil entre deux ouvertures.
  const pendingRec = (rgpdPending || []).find(p => p.sid === sid && p.version === version);
  const [localFirstSeen] = useState(() => pendingRec?.firstSeenAt || now());
  const writeStarted = useRef(false);
  useEffect(() => {
    if (already || !version) return;
    if (pendingRec || writeStarted.current) return;
    writeStarted.current = true;
    (async () => {
      const rec = { sid, version, firstSeenAt: localFirstSeen };
      try {
        const base = await supaGet('rgpdPending7') || [];
        // Un autre appareil a pu écrire entre-temps — ne jamais dupliquer.
        if (base.some(p => p.sid === sid && p.version === version)) { setRgpdPending && setRgpdPending(base); return; }
        const merged = [...base, rec];
        await supaSet('rgpdPending7', merged);
        setRgpdPending && setRgpdPending(merged);
      } catch (e) {
        console.error('[rgpdPending] écriture échouée :', e.message);
        setRgpdPending && setRgpdPending(ps => [...(ps || []), rec]);
      }
    })();
  }, [already, version]);

  const firstSeenAt = pendingRec?.firstSeenAt || localFirstSeen;
  const pastReportWindow = firstSeenAt && (Date.now() - new Date(firstSeenAt).getTime()) >= RGPD_REPORT_WINDOW_MS;

  // Fermeture "reportée" pour CETTE session (remonte au montage suivant — donc à la
  // prochaine ouverture de l'appli, cf en-tête de fichier).
  const [closedThisSession, setClosedThisSession] = useState(false);

  // Anti-esquive : défilement jusqu'en bas + délai minimum, indépendants l'un de l'autre.
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [delayDone, setDelayDone] = useState(false);
  const bodyRef = useRef(null);
  useEffect(() => {
    if (already) return;
    const t = setTimeout(() => setDelayDone(true), RGPD_SCROLL_DELAY_MS);
    return () => clearTimeout(t);
  }, [already]);
  const onScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) setScrolledEnd(true);
  };
  // Contenu qui tiendrait entièrement à l'écran sans scroll possible (petit écran large,
  // texte court) : ne doit jamais bloquer indéfiniment sur `scrolledEnd`. Vérifié après
  // montage (layout stabilisé), une seule fois.
  useEffect(() => {
    if (already) return;
    const id = setTimeout(() => {
      const el = bodyRef.current;
      if (el && el.scrollHeight <= el.clientHeight + 12) setScrolledEnd(true);
    }, 300);
    return () => clearTimeout(id);
  }, [already]);

  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!version || already || closedThisSession) return null;

  const L = (fr, es) => tr(langue, { fr, es });
  const texte = langue === 'es' ? rgpdDoc.texte_es : rgpdDoc.texte_fr;
  const checkboxLabel = langue === 'es' ? rgpdDoc.checkbox_es : rgpdDoc.checkbox_fr;
  const canValidate = scrolledEnd && delayDone && checked && !saving;

  const askClose = () => {
    const msg = L(
      "Ce document réapparaîtra à la prochaine ouverture de l'application. Passé un délai de 24h après sa première apparition, il ne sera plus possible de le reporter.\n\nFermer maintenant ?",
      'Este documento volverá a aparecer la próxima vez que abra la aplicación. Pasado un plazo de 24h desde su primera aparición, ya no será posible posponerlo.\n\n¿Cerrar ahora?'
    );
    if (window.confirm(msg)) setClosedThisSession(true);
  };

  const validate = async () => {
    if (!canValidate) return;
    setSaving(true);
    const rec = { id: 'rga' + Date.now(), sid, version, ts: now(), langue };
    try {
      const base = await supaGet('rgpdAck7') || [];
      const merged = [...base, rec];
      await supaSet('rgpdAck7', merged);
      setRgpdAck && setRgpdAck(merged);
    } catch (e) {
      console.error('[rgpdAck] écriture échouée :', e.message);
      setRgpdAck && setRgpdAck(a => [...(a || []), rec]);
    }
    setSaving(false);
  };

  return (
    <div className="mbk" style={{ zIndex: 2000 }} onClick={() => {}}>
      <div style={{ background: 'var(--bg)', borderRadius: 16, maxWidth: 480, width: '100%', margin: '0 14px', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ background: 'var(--mn)', color: 'white', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="D" style={{ fontSize: 17 }}>🔒 {L('Information — Données personnelles', 'Información — Datos personales')}</div>
          {!pastReportWindow && (
            <button onClick={askClose} style={{ background: 'rgba(255,255,255,.2)', border: 'none', color: 'white', borderRadius: 8, width: 30, height: 30, fontSize: 16, cursor: 'pointer' }} title={L('Fermer (peut réapparaître)', 'Cerrar (puede reaparecer)')}>✕</button>
          )}
        </div>
        <div ref={bodyRef} onScroll={onScroll} style={{ padding: '16px 20px', overflowY: 'auto', fontSize: 13, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
          {texte}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--li)', background: 'var(--wh)' }}>
          {pastReportWindow && (
            <div style={{ background: '#fff4d6', border: '1px solid #e5c977', borderRadius: 8, padding: '8px 10px', fontSize: 11, color: '#7a5a00', marginBottom: 10 }}>
              {L("Le délai de report de 24h est dépassé — ce document doit être validé pour continuer à le voir disparaître de vos écrans.", 'El plazo de aplazamiento de 24h ha vencido — este documento debe validarse para que deje de reaparecer.')}
            </div>
          )}
          {!(scrolledEnd && delayDone) && (
            <div style={{ fontSize: 11, color: 'var(--si)', marginBottom: 10, fontStyle: 'italic' }}>
              {L('Merci de lire le document en entier avant de pouvoir valider.', 'Por favor, lea el documento completo antes de poder validar.')}
            </div>
          )}
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5, lineHeight: 1.4, cursor: (scrolledEnd && delayDone) ? 'pointer' : 'not-allowed', opacity: (scrolledEnd && delayDone) ? 1 : .55, marginBottom: 12 }}>
            <input type="checkbox" checked={checked} disabled={!(scrolledEnd && delayDone)} onChange={e => setChecked(e.target.checked)} style={{ marginTop: 2 }} />
            <span>{checkboxLabel}</span>
          </label>
          <button onClick={validate} disabled={!canValidate} className="btn bok" style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 700, opacity: canValidate ? 1 : .5, cursor: canValidate ? 'pointer' : 'not-allowed' }}>
            {saving ? '…' : L('Valider', 'Validar')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Badge admin — fiche/dossier salarié ─────────────────────────────────────
// Vert dès qu'un accusé existe pour la version en cours de `rgpdDoc7`, sinon un badge
// neutre "en attente". Purement affichage : aucune action ici (voir en-tête de fichier).
function RgpdAckBadge({ sid, rgpdDoc, rgpdAck }) {
  const version = rgpdDoc?.version;
  const ack = rgpdAckFor(rgpdAck, sid, version);
  if (!version) return null;
  if (ack) {
    return (
      <span style={{ background: '#e1f7ee', color: '#2d6b3e', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        ✅ RGPD accepté le {new Date(ack.ts).toLocaleDateString('fr-FR')}
      </span>
    );
  }
  return (
    <span style={{ background: '#fff4d6', color: '#7a5a00', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 999 }}>
      ⏳ RGPD en attente
    </span>
  );
}

export { RgpdInfoModal, RgpdAckBadge };
